import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  writeBatch,
  serverTimestamp,
  addDoc,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Notification, NotificationCreate } from '../types/notification';

/**
 * Subscribe to user's notifications with real-time updates
 * Notifications are sorted by: unread first, then by createdAt descending
 */
export function subscribeToNotifications(
  userId: string,
  onNotifications: (notifications: (Notification & { id: string })[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const notificationsRef = collection(db, 'users', userId, 'notifications');

  // Query for notifications, ordered by read status (false first) then by createdAt
  const q = query(
    notificationsRef,
    orderBy('createdAt', 'desc'),
    limit(20) // Get up to 20 for displaying 10 per group
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const notifications = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as (Notification & { id: string })[];

      onNotifications(notifications);
    },
    (error) => {
      console.error('Error subscribing to notifications:', error);
      onError?.(error);
    }
  );
}

/**
 * Mark a notification as read
 */
export async function markNotificationAsRead(
  userId: string,
  notificationId: string
): Promise<void> {
  const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
  await updateDoc(notificationRef, {
    read: true,
  });
}

/**
 * Mark a notification as unread
 */
export async function markNotificationAsUnread(
  userId: string,
  notificationId: string
): Promise<void> {
  const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
  await updateDoc(notificationRef, {
    read: false,
  });
}

/**
 * Mark all notifications as read for a user
 */
export async function markAllNotificationsAsRead(userId: string): Promise<void> {
  const notificationsRef = collection(db, 'users', userId, 'notifications');
  const q = query(notificationsRef, where('read', '==', false));

  return new Promise((resolve, reject) => {
    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        unsubscribe();

        if (snapshot.empty) {
          resolve(undefined);
          return;
        }

        const batch = writeBatch(db);
        snapshot.docs.forEach((doc) => {
          batch.update(doc.ref, { read: true });
        });

        try {
          await batch.commit();
          resolve(undefined);
        } catch (error) {
          reject(error);
        }
      },
      (error) => {
        unsubscribe();
        reject(error);
      }
    );
  });
}

/**
 * Create a notification for a user (for testing/development)
 * In production, this would typically be done by backend functions
 */
export async function createNotification(
  userId: string,
  notification: NotificationCreate
): Promise<string> {
  const notificationsRef = collection(db, 'users', userId, 'notifications');

  const notificationData: NotificationCreate = {
    ...notification,
    createdAt: serverTimestamp(),
    read: notification.read ?? false,
  };

  const docRef = await addDoc(notificationsRef, notificationData);
  return docRef.id;
}

/**
 * Create multiple notifications for multiple users
 * Useful for organization-wide notifications
 */
export async function createNotificationsForUsers(
  userIds: string[],
  notification: NotificationCreate
): Promise<void> {
  const batch = writeBatch(db);

  const notificationData: NotificationCreate = {
    ...notification,
    createdAt: serverTimestamp(),
    read: notification.read ?? false,
  };

  userIds.forEach((userId) => {
    const notificationsRef = collection(db, 'users', userId, 'notifications');
    const newNotificationRef = doc(notificationsRef);
    batch.set(newNotificationRef, notificationData);
  });

  await batch.commit();
}
